export const getRowAnimationDelay = (rowIndex: number) => {
  return 0.1 * (rowIndex + 1);
};

/** Written as a DOM attribute one side, matched as a CSS selector the other, so
 *  the two cannot be renamed independently. */
export const SKIP_HIGHLIGHT_ATTRIBUTE = "data-skip-highlight";

const NO_LOCKED_IDS: ReadonlySet<never> = new Set();

/**
 * Computes the next selection after a Table `onSelect` change.
 *
 * This is useful for tables that allow filtering *and* bulk-toggling the
 * selection status of rows, since bulk-select-all only sends the rows the table
 * is rendering, so ids hidden by a filter would otherwise be dropped.
 *
 * @param args.current The currently selected ids.
 * @param args.visibleIds The ids that are visible in the table after filtering.
 * @param args.change The meaning of this parameter changes depending on its type:
 *  - If `change` is an id, then its selection status is toggled.
 *  - If `change` is an array, then it is treated as the ids that should now be
 *    selected **among the ids that are visible**. Any ids that _aren't_ visible
 *    will have their selection status preserved.
 * @param args.lockedIds The ids whose selection status cannot be changed by the
 *  table. Defaults to none.
 */
export function nextRowSelection<T extends string | number>({
  current,
  visibleIds,
  change,
  lockedIds = NO_LOCKED_IDS,
}: {
  current: T[];
  visibleIds: ReadonlySet<T>;
  change: T | T[];
  lockedIds?: ReadonlySet<T>;
}): T[] {
  if (Array.isArray(change)) {
    const picked = new Set(change);

    return [
      // Preserve the selection status of all ids that _aren't_ visible
      ...current.filter((id) => !visibleIds.has(id)),
      // Among the visible ids:
      // - If an id is locked, preserve its selection status
      // - Otherwise, take its updated selection status from `picked`
      ...[...visibleIds].filter((id) =>
        lockedIds.has(id) ? current.includes(id) : picked.has(id),
      ),
    ];
  } else {
    if (lockedIds.has(change)) {
      // Not allowed to update the selection status of lockedIds
      return current;
    }

    // Toggle the selection status of `change`
    if (current.includes(change)) {
      return current.filter((id) => id !== change);
    } else {
      return [...current, change];
    }
  }
}
